import { FORBIDDEN_PERSISTENCE_KEYS } from './types.js';

export class ForbiddenPersistenceKeyError extends Error {
  constructor(public readonly paths: string[]) {
    super(`Forbidden persistence key(s): ${paths.join(', ')}`);
    this.name = 'ForbiddenPersistenceKeyError';
  }
}

/** Recursively detect forbidden keys; returns dotted paths (e.g. answers.q1.apiKey). */
export function findForbiddenPersistenceKeyPaths(
  value: unknown,
  prefix = '',
): string[] {
  const paths: string[] = [];
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      paths.push(...findForbiddenPersistenceKeyPaths(item, prefix ? `${prefix}[${index}]` : `[${index}]`));
    });
    return paths;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if ((FORBIDDEN_PERSISTENCE_KEYS as readonly string[]).includes(k)) {
        paths.push(path);
      }
      paths.push(...findForbiddenPersistenceKeyPaths(v, path));
    }
  }
  return paths;
}

export function containsForbiddenPersistenceKeys(raw: unknown): boolean {
  return findForbiddenPersistenceKeyPaths(raw).length > 0;
}

/** Strip forbidden keys recursively (parse-time sanitizer). */
export function stripForbiddenPersistenceKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripForbiddenPersistenceKeys);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if ((FORBIDDEN_PERSISTENCE_KEYS as readonly string[]).includes(k)) continue;
      out[k] = stripForbiddenPersistenceKeys(v);
    }
    return out;
  }
  return value;
}

/** Reject before persistence write. */
export function assertPersistable(value: unknown): void {
  const paths = findForbiddenPersistenceKeyPaths(value);
  if (paths.length > 0) {
    throw new ForbiddenPersistenceKeyError(paths);
  }
}
